import { Data, UI, Utils } from 'common';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import React, { useEffect, useState } from 'react';

const { States } = Data;
const { Util, Validation } = Utils;
const { WrappedInputTextWithLabel, WrappedDropdownWithLabel } = UI;

// run validation rules
export const validate = (name, value) => {
	if (name === 'FirstName') {
		return value === '' ? 'First name is required.' : false;
	} else if (name === 'LastName') {
		return value === '' ? 'Last name is required.' : false;
	} else if (name === 'Address1') {
		return value === '' ? 'Address is required.' : false;
	} else if (name === 'City') {
		return value === '' ? 'City is required.' : false;
	} else if (name === 'Zip') {
		if (value === '') {
			return 'Zip is required.';
		} else {
			return !Validation.isValidZip(value) ? 'Invalid ZIP Code.' : false;
		}
	} else if (name === 'PhoneNumber') {
		if (value === '') {
			return 'Phone number is required.';
		} else {
			return !Validation.isValidPhoneWithExt(value) &&
				!Validation.isValidPhone(value)
				? 'Invalid Phone number.'
				: false;
		}
	} else if (name === 'Email') {
		if (value === '') {
			return 'Email is required.';
		} else {
			return !Validation.isValidEmail(value) ? 'Invalid Email Address.' : false;
		}
	}
};

const ContactInfoForms = ({
	contactInfoName,
	contactInfo = {},
	contactInfoErrors = {},
	isMobile,
	mobileClassName,
	readOnlyContact,
	sameAsMainContact,
	handleInputChange,
	handleValidationOnBlur,
	onKeyPressEnter,
}) => {
	// state holding all fields validation errors
	const [fieldError, setFieldError] = useState({});

	// clear all fields validation if "Same As Main Contact" is checked
	useEffect(() => {
		if (sameAsMainContact === true) {
			setFieldError({});
		}
	}, [sameAsMainContact]);

	// update fieldError when contactInfoErrors is udpated
	useEffect(() => {
		setFieldError(contactInfoErrors);
	}, [contactInfoErrors]);

	const getInputValue = inputName => {
		const contactInfoAddress = isObject(contactInfo.Address)
			? contactInfo.Address
			: {};

		if (isEmpty(contactInfo)) {
			return '';
		} else if (
			includes(['Address1', 'Address2', 'City', 'State', 'Zip'], inputName)
		) {
			return isString(contactInfoAddress[inputName])
				? contactInfoAddress[inputName]
				: '';
		} else {
			return isString(contactInfo[inputName]) ? contactInfo[inputName] : '';
		}
	};

	// pass the updated contactInfo object to the parent
	const onFieldChange = e => {
		const { name, value } = e.target;

		if (
			includes(
				['Address1', 'Address2', 'City', 'Country', 'State', 'Zip'],
				name
			)
		) {
			handleInputChange(contactInfoName, {
				...contactInfo,
				Address: {
					...contactInfo.Address,
					[name]: name === 'Zip' ? Util.formatZip(value) : value,
				},
			});
		} else {
			handleInputChange(contactInfoName, {
				...contactInfo,
				[name]: name === 'PhoneNumber' ? Util.formatPhoneNumber(value) : value,
			});
		}
	};

	// validate and generate the error message if value is invalid, and
	// pass the result to the parent
	const onFieldBlur = e => {
		const { name, value } = e.target;

		// validate the field value
		const newFieldError = {
			...fieldError,
			[name]: validate(name, value),
		};

		// save the validation result
		setFieldError(newFieldError);

		// notify the parent about the validation result
		handleValidationOnBlur(contactInfoName, newFieldError);
	};

	return (
		<>
			<WrappedInputTextWithLabel
				showErrorBelow={isMobile}
				isInputInvalid={contactInfoErrors.FirstName !== false}
				errorMessage={fieldError.FirstName}
				className={`inputtext ${mobileClassName}`}
				label="First Name:"
				uiElementConfig={{
					type: 'text',
					name: 'FirstName',
					value: getInputValue('FirstName'),
					maxLength: 40,
					required: true,
					placeholder: 'First Name',
					dataqa: `${contactInfoName}-firstname`,
					onChange: onFieldChange,
					onBlur: onFieldBlur,
					onKeyPress: onKeyPressEnter,
					disabled: readOnlyContact || sameAsMainContact,
				}}
			/>
			<WrappedInputTextWithLabel
				showErrorBelow={isMobile}
				isInputInvalid={contactInfoErrors.LastName !== false}
				errorMessage={fieldError.LastName}
				className={`inputtext ${mobileClassName}`}
				label="Last Name:"
				uiElementConfig={{
					type: 'text',
					name: 'LastName',
					value: getInputValue('LastName'),
					maxLength: 40,
					required: true,
					placeholder: 'Last Name',
					dataqa: `${contactInfoName}-lastname`,
					onChange: onFieldChange,
					onBlur: onFieldBlur,
					onKeyPress: onKeyPressEnter,
					disabled: readOnlyContact || sameAsMainContact,
				}}
			/>
			<WrappedInputTextWithLabel
				className={`inputtext ${mobileClassName}`}
				label="Company Name:"
				uiElementConfig={{
					type: 'text',
					name: 'CompanyName',
					value: getInputValue('CompanyName'),
					maxLength: 50,
					placeholder: 'Company Name',
					dataqa: `${contactInfoName}-companyname`,
					onChange: onFieldChange,
					disabled: readOnlyContact || sameAsMainContact,
				}}
			/>
			<WrappedInputTextWithLabel
				showErrorBelow={isMobile}
				isInputInvalid={contactInfoErrors.Address1 !== false}
				errorMessage={fieldError.Address1}
				className={`inputtext ${mobileClassName}`}
				label="Address Line 1:"
				uiElementConfig={{
					type: 'text',
					name: 'Address1',
					value: getInputValue('Address1'),
					maxLength: 50,
					required: true,
					placeholder: 'Address Line 1',
					dataqa: `${contactInfoName}-address1`,
					onChange: onFieldChange,
					onBlur: onFieldBlur,
					onKeyPress: onKeyPressEnter,
					disabled: readOnlyContact || sameAsMainContact,
				}}
			/>
			<WrappedInputTextWithLabel
				className={`inputtext ${mobileClassName}`}
				label="Address Line 2:"
				uiElementConfig={{
					type: 'text',
					name: 'Address2',
					value: getInputValue('Address2'),
					maxLength: 50,
					placeholder: 'Address Line 2',
					dataqa: `${contactInfoName}-address2`,
					onChange: onFieldChange,
					disabled: readOnlyContact || sameAsMainContact,
				}}
			/>
			<WrappedInputTextWithLabel
				showErrorBelow={isMobile}
				isInputInvalid={contactInfoErrors.City !== false}
				errorMessage={fieldError.City}
				className={`inputtext ${mobileClassName}`}
				label="City:"
				uiElementConfig={{
					type: 'text',
					name: 'City',
					value: getInputValue('City'),
					maxLength: 30,
					required: true,
					placeholder: 'City',
					dataqa: `${contactInfoName}-city`,
					onChange: onFieldChange,
					onBlur: onFieldBlur,
					onKeyPress: onKeyPressEnter,
					disabled: readOnlyContact || sameAsMainContact,
				}}
			/>
			<WrappedDropdownWithLabel
				className={`dropdown ${mobileClassName}`}
				label="State:"
				uiElementConfig={{
					name: 'State',
					value: getInputValue('State'),
					placeholder: 'State',
					options: States,
					optionLabel: 'name',
					optionValue: 'abbreviation',
					dataqa: `${contactInfoName}-state`,
					onChange: onFieldChange,
					disabled: readOnlyContact || sameAsMainContact,
				}}
			/>
			<WrappedInputTextWithLabel
				showErrorBelow={isMobile}
				isInputInvalid={contactInfoErrors.Zip !== false}
				errorMessage={fieldError.Zip}
				className={`inputtext ${mobileClassName}`}
				label="ZIP Code:"
				uiElementConfig={{
					type: 'text',
					name: 'Zip',
					value: getInputValue('Zip'),
					maxLength: 10,
					required: true,
					placeholder: 'ZIP Code',
					dataqa: `${contactInfoName}-zip`,
					onChange: onFieldChange,
					onBlur: onFieldBlur,
					onKeyPress: onKeyPressEnter,
					disabled: readOnlyContact || sameAsMainContact,
				}}
			/>
			<WrappedInputTextWithLabel
				showErrorBelow={isMobile}
				isInputInvalid={contactInfoErrors.PhoneNumber !== false}
				errorMessage={fieldError.PhoneNumber}
				className={`inputtext ${mobileClassName}`}
				label="Phone Number:"
				uiElementConfig={{
					type: 'text',
					name: 'PhoneNumber',
					value: getInputValue('PhoneNumber'),
					maxLength: 23,
					required: true,
					placeholder: 'Phone Number',
					dataqa: `${contactInfoName}-phonenumber`,
					onChange: onFieldChange,
					onBlur: onFieldBlur,
					onKeyPress: onKeyPressEnter,
					disabled: readOnlyContact || sameAsMainContact,
				}}
			/>
			<WrappedInputTextWithLabel
				showErrorBelow={isMobile}
				isInputInvalid={contactInfoErrors.Email !== false}
				errorMessage={fieldError.Email}
				className={`inputtext ${mobileClassName}`}
				label="Email:"
				uiElementConfig={{
					type: 'text',
					name: 'Email',
					value: getInputValue('Email'),
					maxLength: 255,
					required: true,
					placeholder: 'Email Address',
					dataqa: `${contactInfoName}-email`,
					onChange: onFieldChange,
					onBlur: onFieldBlur,
					onKeyPress: onKeyPressEnter,
					disabled: readOnlyContact || sameAsMainContact,
				}}
			/>
		</>
	);
};

export default ContactInfoForms;
